import React, { TransitionEventHandler, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { mergeRefs } from '@insify/ui-utils';
import { useCarousel } from './CarouselContext';
import { getTransitionDuration } from './utils';

import * as styles from './Carousel.module.css';

type CarouselSliderProps = Omit<JSX.IntrinsicElements['div'], 'children'> & {
  children?: React.ReactElement | React.ReactElement[];
};

export const CarouselSlider: React.FC<CarouselSliderProps> = ({ children, ...props }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const {
    internalState,
    carouselState: { currentIndex, currentConfig },
    navigate,
    onNavigationEnd,
    startAutoplay,
    stopAutoplay,
    setSlideCount,
  } = useCarousel();

  const swipeableHandlers = useSwipeable({
    onSwipedLeft: () => navigate(1),
    onSwipedRight: () => navigate(-1),
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  const { slidesToShow, slideGap, transitionTimingFunction, pauseAutoplayOnHover, infinite } = currentConfig;

  // Effect to initialize or update carousel metadata when the children or the config gets updated
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.setProperty('--slidesToShow', `${slidesToShow}`);
      sliderRef.current.style.setProperty('--slideGap', `${slideGap}`);
    }

    const slideCount = React.Children.count(children);
    const pageCount = infinite ? slideCount : slideCount - (slidesToShow - 1);

    if (slidesToShow > slideCount) {
      throw new Error(`'slidesToShow' (${slidesToShow}) cannot be greater than the number of slides (${slideCount}).`);
    }

    setSlideCount(slideCount);
    internalState.current.slideCount = slideCount;
    internalState.current.pageCount = pageCount;
    internalState.current.allowNavigation = slidesToShow < slideCount;
  }, [children, slidesToShow, slideGap, infinite]);

  // Effect to handle slide animation after the current index changes
  useEffect(() => {
    if (!trackRef.current) return;

    if (isNaN(internalState.current.prevIndex)) {
      trackRef.current.style.transition = '';
      trackRef.current.style.transform = '';
      return updateSlideOrder();
    }

    internalState.current.allowNavigation = false;
    const transitionValue = `transform ${getTransitionDuration(
      internalState.current.navigationMode,
      currentConfig,
    )}ms ${transitionTimingFunction}`;

    if (infinite) {
      const transformValue = `translateX(calc((100% - (${slideGap} * (${
        slidesToShow - 1
      }))) / -${slidesToShow} - ${slideGap}))`;

      if (internalState.current.navigationDelta === 1) {
        trackRef.current.style.transition = transitionValue;
        trackRef.current.style.transform = transformValue;
      } else {
        trackRef.current.style.transform = transformValue;
        trackRef.current.offsetHeight; // Triggers reflow to apply style change immediately
        trackRef.current.style.transition = transitionValue;
        trackRef.current.style.transform = ``;
        updateSlideOrder();
      }
    } else {
      trackRef.current.style.transition = transitionValue;
      trackRef.current.style.transform = `translateX(calc(${currentIndex} * ((100% - (${slideGap} * (${
        slidesToShow - 1
      }))) / -${slidesToShow} - ${slideGap})))`;
    }

    timeoutRef.current = setTimeout(
      () => {
        if (!internalState.current.allowNavigation) cleanupAfterNavigation();
      },
      getTransitionDuration(internalState.current.navigationMode, currentConfig) + 50,
    );
  }, [currentIndex]);

  const updateSlideOrder = () => {
    if (!trackRef.current) return;

    for (let i = 0; i < trackRef.current.children.length; i++) {
      const indexDiff = i - currentIndex;
      const value = i >= currentIndex ? indexDiff : internalState.current.pageCount + indexDiff;
      (trackRef.current.children[i] as HTMLElement).style.order = `${value}`;
    }
  };

  const transitionEndHandler: TransitionEventHandler = ({ propertyName }) => {
    if (propertyName === 'transform') cleanupAfterNavigation();
  };

  const cleanupAfterNavigation = () => {
    if (!trackRef.current) return;

    clearTimeout(timeoutRef.current);

    if (infinite) {
      trackRef.current.style.transition = '';
      trackRef.current.style.transform = '';
      updateSlideOrder();
    }

    internalState.current.allowNavigation = true;
    onNavigationEnd?.({ currentIndex, prevIndex: internalState.current.prevIndex });
  };

  return (
    <div
      {...props}
      ref={mergeRefs(sliderRef, swipeableHandlers.ref)}
      onMouseDown={(event) => {
        swipeableHandlers.onMouseDown?.(event);
        props.onMouseDown?.(event);
      }}
      className={props.className}
      style={
        {
          '--slideWidth': `calc((100% - (var(--slidesToShow) - 1) * var(--slideGap)) / var(--slidesToShow))`,
          ...props.style,
        } as React.CSSProperties
      }
    >
      <div
        className={styles.container}
        onMouseEnter={
          pauseAutoplayOnHover
            ? () => {
                internalState.current.autoplayPaused = true;
                stopAutoplay();
              }
            : undefined
        }
        onMouseLeave={
          pauseAutoplayOnHover
            ? () => {
                internalState.current.autoplayPaused = false;
                startAutoplay();
              }
            : undefined
        }
      >
        <div ref={trackRef} className={styles.track} onTransitionEnd={transitionEndHandler}>
          {children}
        </div>
      </div>
    </div>
  );
};
